import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { poolPredictionsAPI, poolAdminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Plus, Trophy, Settings, TrendingUp, DollarSign, Users, CheckCircle2, XCircle, Clock } from 'lucide-react';

const emptyMarketForm = {
  title: '',
  description: '',
  category: 'Football',
  currency: 'ZAR',
  entry_fee: 100,
  min_entry_fee: 100,
  max_entry_fee: 1000,
  min_participants: 2,
  max_participants: 1000,
  closes_at: '',
  outcomes: [{ name: '', description: '', estimated_odds: 2.0 }, { name: '', description: '', estimated_odds: 2.0 }]
};

export const AdminPoolPredictionsPage = () => {
  const [markets, setMarkets] = useState([]);
  const [pendingSettlements, setPendingSettlements] = useState([]);
  const [allSettlements, setAllSettlements] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialogs
  const [createMarketOpen, setCreateMarketOpen] = useState(false);
  const [marketForm, setMarketForm] = useState(emptyMarketForm);
  const [settleDialogOpen, setSettleDialogOpen] = useState(null);
  const [winningOutcome, setWinningOutcome] = useState('');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configForm, setConfigForm] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [marketsRes, pendingRes, settlementsRes, analyticsRes, configRes] = await Promise.all([
        poolPredictionsAPI.listMarkets(null, null, 200),
        poolAdminAPI.listPendingSettlements(),
        poolAdminAPI.listAllSettlements(),
        poolAdminAPI.getAnalytics(),
        poolAdminAPI.getConfig()
      ]);
      setMarkets(marketsRes.data);
      setPendingSettlements(pendingRes.data);
      setAllSettlements(settlementsRes.data);
      setAnalytics(analyticsRes.data);
      setConfig(configRes.data);
      setConfigForm(configRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const addOutcome = () => {
    setMarketForm({
      ...marketForm,
      outcomes: [...marketForm.outcomes, { name: '', description: '', estimated_odds: 2.0 }]
    });
  };

  const removeOutcome = (idx) => {
    if (marketForm.outcomes.length <= 2) {
      toast.error('At least 2 outcomes required');
      return;
    }
    setMarketForm({
      ...marketForm,
      outcomes: marketForm.outcomes.filter((_, i) => i !== idx)
    });
  };

  const updateOutcome = (idx, field, value) => {
    const newOutcomes = [...marketForm.outcomes];
    newOutcomes[idx] = {
      ...newOutcomes[idx],
      [field]: field === 'estimated_odds' ? parseFloat(value) : value
    };
    setMarketForm({ ...marketForm, outcomes: newOutcomes });
  };

  const handleCreateMarket = async (e) => {
    e.preventDefault();
    
    if (!marketForm.title || !marketForm.description || !marketForm.closes_at) {
      toast.error('Fill all required fields');
      return;
    }
    
    if (marketForm.outcomes.length < 2 || marketForm.outcomes.some(o => !o.name)) {
      toast.error('At least 2 outcomes with names required');
      return;
    }

    try {
      await poolAdminAPI.createMarket(marketForm);
      toast.success('Market created successfully!');
      setCreateMarketOpen(false);
      setMarketForm(emptyMarketForm);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to create market');
    }
  };

  const handlePublishMarket = async (marketId) => {
    if (!window.confirm('Publish this market? It will be open for participants.')) return;
    try {
      await poolAdminAPI.publishMarket(marketId);
      toast.success('Market published!');
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to publish');
    }
  };

  const handleCloseMarket = async (marketId) => {
    if (!window.confirm('Close this market? No more participants can join.')) return;
    try {
      await poolAdminAPI.closeMarket(marketId);
      toast.success('Market closed!');
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to close');
    }
  };

  const openSettleDialog = (market) => {
    setSettleDialogOpen(market);
    setWinningOutcome(market.outcomes[0]?.name || '');
  };

  const handleInitiateSettlement = async () => {
    if (!winningOutcome) {
      toast.error('Select winning outcome');
      return;
    }

    try {
      const { data } = await poolAdminAPI.initiateSettlement(settleDialogOpen.id, winningOutcome);
      
      if (data.requires_approval) {
        toast.success(`Settlement initiated! Requires admin approval (${data.total_payout} payout)`);
      } else {
        toast.success(`Settlement completed! ${data.num_winners} winners paid`);
      }
      
      setSettleDialogOpen(null);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Settlement failed');
    }
  };

  const handleApproveSettlement = async (settlementId) => {
    if (!window.confirm('Approve this large payout settlement?')) return;
    
    try {
      const { data } = await poolAdminAPI.approveSettlement(settlementId);
      toast.success(`Settlement approved and executed! ${data.successful_payouts} payouts completed`);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to approve');
    }
  };

  const handleCancelMarket = async (marketId) => {
    const reason = window.prompt('Enter cancellation reason:');
    if (!reason) return;
    
    try {
      const { data } = await poolAdminAPI.cancelMarket(marketId, reason);
      toast.success(`Market cancelled! ${data.refunds_processed} participants refunded`);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to cancel');
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await poolAdminAPI.updateConfig(configForm);
      toast.success('Configuration updated!');
      setConfigDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update config');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-500';
      case 'closed': return 'bg-yellow-500';
      case 'pending_settlement': return 'bg-orange-500';
      case 'settled': return 'bg-blue-500';
      case 'cancelled': return 'bg-red-500';
      case 'draft': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const getSettlementStatusColor = (status) => {
    switch (status) {
      case 'awaiting_approval': return 'bg-orange-500';
      case 'approved': return 'bg-green-500';
      case 'completed': return 'bg-blue-500';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Trophy className="h-8 w-8 text-yellow-500" />
              Pool-Based Predictions
            </h1>
            <p className="text-muted-foreground mt-1">Manage prediction markets and settlements</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setConfigDialogOpen(true)} variant="outline" className="gap-2">
              <Settings className="h-4 w-4" />
              Settings
            </Button>
            <Button onClick={() => setCreateMarketOpen(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Create Market
            </Button>
          </div>
        </div>

        {/* Analytics Cards */}
        {analytics && (
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Markets</CardTitle>
                <Trophy className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.markets?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {analytics.markets?.open || 0} open · {analytics.markets?.closed || 0} closed
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Participants</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.participants?.total || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {analytics.participants?.active || 0} active
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Pool Value</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.pools?.total_staked?.toFixed(2) || '0.00'}</div>
                <p className="text-xs text-muted-foreground">
                  Avg: {analytics.pools?.average_stake?.toFixed(2) || '0.00'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Platform Revenue</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.settlements?.platform_revenue?.toFixed(2) || '0.00'}</div>
                <p className="text-xs text-muted-foreground">
                  {analytics.settlements?.completed || 0} settlements
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pending Approvals Alert */}
        {pendingSettlements.length > 0 && (
          <Card className="border-orange-500 border-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-600">
                <Clock className="h-5 w-5" />
                {pendingSettlements.length} Settlement{pendingSettlements.length > 1 ? 's' : ''} Awaiting Approval
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingSettlements.map((settlement) => (
                  <div key={settlement.id} className="flex items-center justify-between p-3 bg-orange-50 rounded">
                    <div>
                      <p className="font-medium">{settlement.market_title}</p>
                      <p className="text-sm text-muted-foreground">
                        {settlement.num_winners} winners · Total payout: {settlement.total_payout?.toFixed(2)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleApproveSettlement(settlement.id)}
                      className="gap-2"
                    >
                      <CheckCircle2 className="h-4 w-4" />
                      Approve
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="markets" className="w-full">
          <TabsList>
            <TabsTrigger value="markets">Markets ({markets.length})</TabsTrigger>
            <TabsTrigger value="settlements">Settlements ({allSettlements.length})</TabsTrigger>
          </TabsList>

          {/* Markets Tab */}
          <TabsContent value="markets" className="mt-6">
            {loading ? (
              <p className="text-center py-8">Loading...</p>
            ) : markets.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trophy className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No markets created yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {markets.map((market) => (
                  <Card key={market.id}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg">{market.title}</CardTitle>
                          <p className="text-sm text-muted-foreground mt-1">{market.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                            <span>{market.category}</span>
                            <span>·</span>
                            <span>{market.participants_count || 0} participants</span>
                            <span>·</span>
                            <span>Pool: {market.current_pool?.toFixed(2) || '0.00'} {market.currency}</span>
                          </div>
                        </div>
                        <Badge className={getStatusColor(market.status)}>{market.status}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {market.status === 'draft' && (
                          <Button size="sm" onClick={() => handlePublishMarket(market.id)}>
                            Publish
                          </Button>
                        )}
                        {market.status === 'open' && (
                          <Button size="sm" variant="outline" onClick={() => handleCloseMarket(market.id)}>
                            Close
                          </Button>
                        )}
                        {market.status === 'closed' && (
                          <Button size="sm" onClick={() => openSettleDialog(market)}>
                            Settle
                          </Button>
                        )}
                        {['draft', 'open'].includes(market.status) && (
                          <Button size="sm" variant="destructive" onClick={() => handleCancelMarket(market.id)}>
                            Cancel & Refund
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Settlements Tab */}
          <TabsContent value="settlements" className="mt-6">
            {allSettlements.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">No settlements yet</p>
                </CardContent>
              </Card>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Market</TableHead>
                    <TableHead>Winning Outcome</TableHead>
                    <TableHead>Winners</TableHead>
                    <TableHead>Total Payout</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allSettlements.map((settlement) => (
                    <TableRow key={settlement.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{settlement.market_title}</p>
                          <p className="text-xs text-muted-foreground">{settlement.market_category}</p>
                        </div>
                      </TableCell>
                      <TableCell>{settlement.winning_outcome}</TableCell>
                      <TableCell>{settlement.num_winners}</TableCell>
                      <TableCell>{settlement.total_payout?.toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={getSettlementStatusColor(settlement.status)}>
                          {settlement.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {settlement.status === 'awaiting_approval' && (
                          <Button
                            size="sm"
                            onClick={() => handleApproveSettlement(settlement.id)}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Market Dialog */}
      <Dialog open={createMarketOpen} onOpenChange={setCreateMarketOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Prediction Market</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateMarket} className="space-y-4">
            <div>
              <Label>Title*</Label>
              <Input
                value={marketForm.title}
                onChange={(e) => setMarketForm({ ...marketForm, title: e.target.value })}
                placeholder="E.g., Manchester United vs Liverpool"
                required
              />
            </div>

            <div>
              <Label>Description*</Label>
              <Textarea
                value={marketForm.description}
                onChange={(e) => setMarketForm({ ...marketForm, description: e.target.value })}
                placeholder="Describe the prediction market..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category*</Label>
                <select
                  className="w-full p-2 border rounded"
                  value={marketForm.category}
                  onChange={(e) => setMarketForm({ ...marketForm, category: e.target.value })}
                >
                  <option value="Football">Football</option>
                  <option value="Basketball">Basketball</option>
                  <option value="Cricket">Cricket</option>
                  <option value="Politics">Politics</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <Label>Currency*</Label>
                <select
                  className="w-full p-2 border rounded"
                  value={marketForm.currency}
                  onChange={(e) => setMarketForm({ ...marketForm, currency: e.target.value })}
                >
                  <option value="ZAR">ZAR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Entry Fee*</Label>
                <Input
                  type="number"
                  value={marketForm.entry_fee}
                  onChange={(e) => setMarketForm({ ...marketForm, entry_fee: parseFloat(e.target.value) })}
                  required
                />
              </div>
              <div>
                <Label>Min Entry Fee</Label>
                <Input
                  type="number"
                  value={marketForm.min_entry_fee}
                  onChange={(e) => setMarketForm({ ...marketForm, min_entry_fee: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Max Entry Fee</Label>
                <Input
                  type="number"
                  value={marketForm.max_entry_fee}
                  onChange={(e) => setMarketForm({ ...marketForm, max_entry_fee: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Participants</Label>
                <Input
                  type="number"
                  value={marketForm.min_participants}
                  onChange={(e) => setMarketForm({ ...marketForm, min_participants: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <Label>Max Participants</Label>
                <Input
                  type="number"
                  value={marketForm.max_participants}
                  onChange={(e) => setMarketForm({ ...marketForm, max_participants: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Closes At (UTC)*</Label>
              <Input
                type="datetime-local"
                value={marketForm.closes_at}
                onChange={(e) => setMarketForm({ ...marketForm, closes_at: e.target.value })}
                required
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Outcomes* (at least 2)</Label>
                <Button type="button" size="sm" onClick={addOutcome}>
                  Add Outcome
                </Button>
              </div>
              <div className="space-y-3">
                {marketForm.outcomes.map((outcome, idx) => (
                  <div key={idx} className="flex gap-2 items-start p-3 border rounded">
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder="Outcome name*"
                        value={outcome.name}
                        onChange={(e) => updateOutcome(idx, 'name', e.target.value)}
                        required
                      />
                      <Input
                        placeholder="Description (optional)"
                        value={outcome.description}
                        onChange={(e) => updateOutcome(idx, 'description', e.target.value)}
                      />
                      <Input
                        type="number"
                        step="0.1"
                        placeholder="Estimated odds"
                        value={outcome.estimated_odds}
                        onChange={(e) => updateOutcome(idx, 'estimated_odds', e.target.value)}
                      />
                    </div>
                    {marketForm.outcomes.length > 2 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => removeOutcome(idx)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateMarketOpen(false)}>
                Cancel
              </Button>
              <Button type="submit">Create Market</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Settle Market Dialog */}
      <Dialog open={!!settleDialogOpen} onOpenChange={() => setSettleDialogOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle Market</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="font-medium">{settleDialogOpen?.title}</p>
              <p className="text-sm text-muted-foreground mt-1">{settleDialogOpen?.description}</p>
            </div>

            <div>
              <Label>Select Winning Outcome</Label>
              <select
                className="w-full mt-1 p-2 border rounded"
                value={winningOutcome}
                onChange={(e) => setWinningOutcome(e.target.value)}
              >
                {settleDialogOpen?.outcomes?.map((outcome) => (
                  <option key={outcome.name} value={outcome.name}>
                    {outcome.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="p-3 bg-muted rounded text-sm">
              <p className="text-muted-foreground">
                This will calculate payouts and distribute winnings to participants who selected the winning outcome.
                {config && config.large_payout_threshold && (
                  <span className="block mt-2">
                    <strong>Note:</strong> Payouts above {config.large_payout_threshold} will require admin approval.
                  </span>
                )}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleDialogOpen(null)}>Cancel</Button>
            <Button onClick={handleInitiateSettlement}>Settle Market</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Config Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Prediction System Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Platform Fee (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={configForm.platform_fee_percent}
                  onChange={(e) => setConfigForm({ ...configForm, platform_fee_percent: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Treasury (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={configForm.treasury_percent}
                  onChange={(e) => setConfigForm({ ...configForm, treasury_percent: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Referral (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={configForm.referral_percent}
                  onChange={(e) => setConfigForm({ ...configForm, referral_percent: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Jackpot (%)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={configForm.jackpot_percent}
                  onChange={(e) => setConfigForm({ ...configForm, jackpot_percent: parseFloat(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Large Payout Threshold (requires approval)</Label>
              <Input
                type="number"
                value={configForm.large_payout_threshold}
                onChange={(e) => setConfigForm({ ...configForm, large_payout_threshold: parseFloat(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Payouts above this amount will require admin approval before execution
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Min Entry Fee</Label>
                <Input
                  type="number"
                  value={configForm.min_entry_fee}
                  onChange={(e) => setConfigForm({ ...configForm, min_entry_fee: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Max Entry Fee</Label>
                <Input
                  type="number"
                  value={configForm.max_entry_fee}
                  onChange={(e) => setConfigForm({ ...configForm, max_entry_fee: parseFloat(e.target.value) })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateConfig}>Save Configuration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
